import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fireActivityPage } from '../lib/claudeApi';
import { API_BASE } from '../lib/config';
import FeedbackButton from '../components/FeedbackButton';

const TYPE_COLORS = {
  food: '#c9a84c',
  culture: '#7eb8c9',
  nature: '#7ec49b',
  adventure: '#e07b6b',
  wellness: '#b8a0e8',
  nightlife: '#e0a0c8',
  history: '#a0b8e0',
  shopping: '#e0c87b',
};

const PROFILE_INSIGHTS = {
  'The Pioneer': 'As The Pioneer, you travel to places most people will discover years from now. The activities ahead were chosen for someone who wants the real version of a destination, not the performance of it.',
  'The Adventurer': 'As The Adventurer, you come home with stories nobody else at the table has heard. The activities ahead lean toward discovery, local access, and experiences that do not appear in guidebooks.',
  'The Explorer': 'As The Explorer, you use the iconic as an anchor and find the real city beyond it. The activities ahead mix the essential with the genuinely unexpected.',
  'The Wanderluster': 'As The Wanderluster, you are here because something pulled you - a photo, a feeling, a recommendation you could not ignore. The activities ahead are chosen to make sure you find what you came for, and a few things you did not know you were looking for.',
  'The Curious Traveler': 'As The Curious Traveler, you leave with a richer picture of a place than most visitors ever find. The activities ahead balance the highlights with local depth.',
  'The Selective Explorer': 'As The Selective Explorer, you appreciate the classics but always find that one thing nobody else at the dinner table has done. The activities ahead include exactly that.',
  'The Classics': 'As The Classics, you know that iconic destinations are iconic for a reason. The activities ahead honor the greatest hits while adding the local layer that most visitors miss.',
};

const ACTIVITY_PROFILE_DESCRIPTIONS = {
  'The Foodie': 'You experience every destination through what it grows, cooks, and serves. Markets at dawn, hole-in-the-wall restaurants locals actually eat at, dishes you have never heard of that become the story you tell when you get home.',
  'The Connoisseur': 'You travel partly for what a place produces. Vineyard visits, craft brewery tours, whisky distilleries, local spirits - understanding what is made here and why is as important as where you eat or what you see.',
  'The Curator': 'You move through a destination like someone who has done the research and knows exactly what deserves your time. Museums, historical sites, architecture, art - nothing is accidental and nothing is wasted.',
  'The Wanderer': 'Water, mountains, forests, open landscapes - you feel most alive when nature is the backdrop. Whether you are hiking a ridge, sitting beside a river, or swimming in something cold and clear.',
  'The Adventurer': 'You want to come home having done something. A climb, a dive, a trail that required more than you expected - the physical challenge is the point, not the obstacle.',
  'The Live Wire': 'You come alive after dark. Live music, the right bar on the right street, a city that does not start until midnight - nightlife is not the end of the day for you, it is often the best part of it.',
  'The Drifter': 'You do not need a plan to have a perfect trip. A good place, enough time, and the freedom to follow whatever feels right - the best moments find you rather than the other way around.',
  'The Restorer': 'You travel to come back to yourself. Spas, slow mornings, quiet coastlines, a long lunch with nowhere to be after - the best trip leaves you genuinely rested.',
  'The Immersionist': 'You want to be inside a place, not observing it. Food, neighborhoods, people, culture, daily life - you go deep across all of it rather than specializing in one dimension.',
  'The Cultural Omnivore': 'History, food, art, local life - you consume a destination across every cultural dimension simultaneously. Culture is not one thing for you, it is everything.',
  'The Maximalist': 'You want everything turned up. Adventure before lunch, a great meal after, live music at night, a hike tomorrow morning. You make the most of every activity, every environment, and every day.',
};

function getActivityProfileDescription(activityProfile) {
  if (!activityProfile) return '';
  if (ACTIVITY_PROFILE_DESCRIPTIONS[activityProfile]) {
    return ACTIVITY_PROFILE_DESCRIPTIONS[activityProfile];
  }
  const withMatch = activityProfile.match(/^(.+?) with an? (.+?) streak$/);
  if (withMatch) {
    const primary = withMatch[1];
    const streak = withMatch[2];
    const base = ACTIVITY_PROFILE_DESCRIPTIONS[primary] || '';
    const streakNote = `With a ${streak} streak woven in, you bring an extra dimension to every destination.`;
    return base ? `${base} ${streakNote}` : streakNote;
  }
  return '';
}

function Activities() {
  const navigate = useNavigate();
  const [destination, setDestination] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [factIndex, setFactIndex] = useState(0);
  const [favorites, setFavorites] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);

  // Load existing favorites on mount
  useEffect(() => {
    const userId = sessionStorage.getItem('carpeUserId');
    if (!userId) return;
    fetch(`${API_BASE}/auth/favorites/${userId}`)
      .then(res => res.json())
      .then(data => {
        const favSet = new Set(
          (data.favorites || [])
            .filter(f => f.type === 'activity')
            .map(f => f.item_data.name)
        );
        setFavorites(favSet);
      })
      .catch(() => {});
  }, []);

  function handleFavoriteActivity(activity) {
    const userId = sessionStorage.getItem('carpeUserId');
    if (!userId) return;
    fetch(`${API_BASE}/auth/favorites/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        type: 'activity',
        item_data: {
          name: activity.name,
          type: activity.type,
          description: activity.description,
          local_tip: activity.local_tip,
        },
      }),
    })
      .then(res => res.json())
      .then(data => {
        setFavorites(prev => {
          const next = new Set(prev);
          if (data.action === 'added') {
            next.add(activity.name);
          } else {
            next.delete(activity.name);
          }
          return next;
        });
      })
      .catch(() => {});
  }

  useEffect(() => {
    const storedDest = sessionStorage.getItem('carpeDestination');
    const storedProfile = sessionStorage.getItem('carpeWonderProfile');
    // Check if Loading.jsx already fetched activities
    const preloadedActivities = sessionStorage.getItem('carpeCurrentActivities');
    if (preloadedActivities && storedDest && storedProfile) {
      const acts = JSON.parse(preloadedActivities);
      setActivities(acts);
      sessionStorage.removeItem('carpeCurrentActivities');
      setDestination(JSON.parse(storedDest));
      setProfile(JSON.parse(storedProfile));
      setLoading(false);
      // Note: no auto-save here. All three callers of carpeCurrentActivities
      // (Welcome.jsx restore, SavedActivities.jsx, History.jsx) are re-displaying
      // data that already exists in Supabase — nothing new to save. The old
      // save-on-mount here was not event_date-aware and could silently overwrite
      // the wrong dated/undated row for the same destination. Removed.
      return;
    }

    if (!storedDest || !storedProfile) {
      navigate('/');
      return;
    }

    const dest = JSON.parse(storedDest);
    const prof = JSON.parse(storedProfile);
    setDestination(dest);
    setProfile(prof);

    // Check if Loading.jsx already populated page 1 via the new rank+enrich pipeline.
    // If so, read from storage and render immediately — no second loading state.
    const existingPool = JSON.parse(sessionStorage.getItem('carpeActivityPool') || '{}');
    const activityCacheKey = dest.event_date ? `${dest.name}::${dest.event_date}` : dest.name;
    const existingPage1 = existingPool[activityCacheKey]?.page1;
    if (existingPage1 && existingPage1.length > 0) {
      setActivities(existingPage1);
      setLoading(false);
      return;
    }

    // Loading.jsx owns all activity generation via rank+enrich pipeline.
    // If we reach here, the pool wasn't populated — navigate back rather
    // than trying to generate activities directly from Activities.jsx.
    setError('Could not load activities. Please try again.');
    setLoading(false);
  }, [navigate]);

  // Rotate facts every 4 seconds while loading
  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setFactIndex(prev => prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [loading]);

  function toggleExpand(index) {
    setExpandedIndex(prev => prev === index ? null : index);
  }

  function getAvailablePages() {
    if (!destination) return 1;
    const pool = JSON.parse(sessionStorage.getItem('carpeActivityPool') || '{}');
    const cacheKey = destination.event_date ? `${destination.name}::${destination.event_date}` : destination.name;
    const destPool = pool[cacheKey] || {};
    return Object.keys(destPool).length;
  }

  function buildProfileForCalls() {
    return {
      profile: profile.profile,
      pace: profile.pace,
      activity_level: profile.activity_level,
      best_for_tags: profile.best_for_tags,
      social_vibe: profile.social_vibe,
      explorer_score: profile.explorer_score,
      activity_profile: profile.activity_profile,
      activity_dimensions: profile.activity_dimensions,
      activity_profile_description: profile.activity_profile_description,
    };
  }
  
  function handleContinue() {
    if (!destination || !profile) return;
    const nextPage = currentPage + 1;
    const pool = JSON.parse(sessionStorage.getItem('carpeActivityPool') || '{}');
    const cacheKey = destination.event_date ? `${destination.name}::${destination.event_date}` : destination.name;
    const destPool = pool[cacheKey] || {};
    const pageKey = `page${nextPage}`;

    // Fire two pages ahead on every Continue click.
    // User clicks to go to page N → page N+2 fires immediately.
    // By the time they finish reading page N and click for N+1,
    // page N+2 is already done or nearly done.
    const futureExclusions = JSON.parse(sessionStorage.getItem('carpeActivityExclusions') || '{}');
    const futureExclusionList = futureExclusions[cacheKey] || [];
    const eventContext = destination.event_date
      ? { lat: destination.lat, lng: destination.lng, event_date: destination.event_date }
      : null;
    fireActivityPage(
      destination.name, destination.country, buildProfileForCalls(),
      nextPage + 2, futureExclusionList, () => {}, null, eventContext, destination.state_province || null
    );

    if (destPool[pageKey]) {
      // Already loaded — show immediately
      setActivities(destPool[pageKey]);
      setExpandedIndex(null);
      setCurrentPage(nextPage);
    } else {
      // Not ready yet — fire it now and wait
      setPageLoading(true);
      const exclusions = JSON.parse(sessionStorage.getItem('carpeActivityExclusions') || '{}');
      const combinedExclusions = exclusions[destination.name] || [];
      fireActivityPage(
        destination.name, destination.country, buildProfileForCalls(),
        nextPage, combinedExclusions, (newActivities) => {
          setActivities(newActivities);
          setExpandedIndex(null);
          setCurrentPage(nextPage);
          setPageLoading(false);
        }
      );
    }
  }

  function handleBack() {
    const referrer = sessionStorage.getItem('carpeActivitiesReferrer');
    sessionStorage.removeItem('carpeActivitiesReferrer');
    if (referrer) {
      navigate(referrer);
    } else {
      const userId = sessionStorage.getItem('carpeUserId');
      if (userId) {
        navigate('/welcome');
      } else {
        navigate('/results');
      }
    }
  }

  const facts = destination?.facts || [];
  const currentFact = facts.length > 0 ? facts[factIndex % facts.length] : null;
  const insight = PROFILE_INSIGHTS[profile?.profile] || '';

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.loadingLogo}>Carpe Wonder™</div>
        <div style={styles.loadingTitle}>
          Finding the best experiences{destination ? ` in ${destination.name}` : ''}...
        </div>

        {insight && (
          <div style={styles.insightBox}>
            <div style={styles.insightText}>{insight}</div>
          </div>
        )}

        {currentFact && (
          <div style={styles.factBox}>
            <div style={styles.factLabel}>Did you know?</div>
            <div style={styles.factText}>{currentFact}</div>
          </div>
        )}

        <div style={styles.loadingDots}>
          <span style={styles.dot} />
          <span style={styles.dot} />
          <span style={styles.dot} />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.errorText}>{error}</div>
        <button style={styles.backBtn} onClick={handleBack}>
          Back to destinations
        </button>
      </div>
    );
  }

  const locationString = destination
    ? [destination.name, destination.state_province, destination.country]
        .filter(Boolean)
        .join(', ')
    : '';

  return (
    <div style={styles.page}>
      <FeedbackButton />
      <div style={styles.container}>
      
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <div style={styles.logo}>Carpe Wonder™</div>
        </div>

        <div style={styles.destHeader}>
          <h1 style={styles.destName}>{destination?.name}</h1>
          <div style={styles.destLocation}>{locationString}</div>
          {profile && (
            <div style={styles.profileTagWrap}>
              <div style={styles.profileTag}>
                Curated for {profile.profile}
              </div>
            </div>
          )}

          {profile?.activity_profile && (
            <div style={styles.activityProfileBanner}>
              <div style={styles.activityProfileName}>{profile.activity_profile}</div>
              <div style={styles.activityProfileDesc}>
                {getActivityProfileDescription(profile.activity_profile)}
              </div>
            </div>
          )}
        </div>

        <p style={styles.sectionSubtitle}>
          Five experiences chosen specifically for how you travel. Tap any one to see the insider tip.
        </p>

        <div style={styles.cardList}>
          {activities.map((activity, index) => {
            const isExpanded = expandedIndex === index;
            const typeColor = TYPE_COLORS[activity.type] || '#c9a84c';

            return (
              <div
                key={index}
                style={{
                  ...styles.card,
                  borderColor: isExpanded
                    ? typeColor
                    : 'rgba(255,255,255,0.08)',
                }}
              >
                <div
                  style={{ ...styles.cardHeader, cursor: 'pointer' }}
                  onClick={() => toggleExpand(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpand(index);
                    }
                  }}
                >
                  <div style={styles.cardLeft}>
                    <div style={styles.activityName}>{activity.name}</div>
                    <div style={{
                      ...styles.typeBadge,
                      background: typeColor + '22',
                      color: typeColor,
                    }}>
                      {activity.type}
                    </div>
                  </div>
                  <div style={styles.activityCardRight}>
                    <button
                      style={styles.heartBtn}
                      onClick={e => {
                        e.stopPropagation();
                        handleFavoriteActivity(activity);
                      }}
                    >
                      {favorites.has(activity.name) ? '❤️' : '🤍'}
                    </button>
                    <div style={styles.chevron}>
                      {isExpanded ? '↑' : '↓'}
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div style={styles.cardBody}>
                    <p style={styles.description}>{activity.description}</p>
                    <div style={styles.tipBox}>
                      <div style={{ ...styles.tipLabel, color: typeColor }}>
                        Local tip
                      </div>
                      <p style={styles.tipText}>{activity.local_tip}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={styles.paginationWrap}>
          <div style={styles.dotsRow}>
            {Array.from({ length: Math.max(getAvailablePages(), currentPage) }).map((_, i) => (
              <span
                key={i}
                style={{
                  ...styles.dot2,
                  background: i + 1 === currentPage ? 'var(--gold)' : 'rgba(255,255,255,0.2)',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            {currentPage > 1 && (
              <button
                style={{ ...styles.continueBtn, background: 'transparent', border: '1px solid var(--gold)', color: 'var(--gold)' }}
                onClick={() => {
                  const pool = JSON.parse(sessionStorage.getItem('carpeActivityPool') || '{}');
                  const cacheKey = destination?.event_date ? `${destination.name}::${destination.event_date}` : destination?.name;
                  const destPool = pool[cacheKey] || {};
                  const prevPageKey = `page${currentPage - 1}`;
                  if (destPool[prevPageKey]) {
                    setActivities(destPool[prevPageKey]);
                    setExpandedIndex(null);
                    setCurrentPage(prev => prev - 1);
                  }
                }}
              >
                ← Back
              </button>
            )}
            <button
              style={{
                ...styles.continueBtn,
                opacity: pageLoading ? 0.6 : 1,
                cursor: pageLoading ? 'default' : 'pointer',
              }}
              onClick={handleContinue}
              disabled={pageLoading}
            >
              {pageLoading ? 'Finding more…' : 'Continue the Wonder →'}
            </button>
          </div>
          <button style={styles.backToResults} onClick={handleBack}>
            ← Back to all destinations
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1f',
    padding: '0 0 48px',
  },
  container: {
    maxWidth: '480px',
    margin: '0 auto',
    padding: '0 20px',
  },
  loadingPage: {
    minHeight: '100vh',
    background: '#0d0d1f',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 32px',
    textAlign: 'center',
    gap: '20px',
  },
  loadingLogo: {
    fontSize: '14px',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  loadingTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#fff',
    lineHeight: '1.4',
  },
  insightBox: {
    border: '0.5px solid rgba(201,168,76,0.25)',
    borderRadius: '14px',
    padding: '16px 20px',
    background: 'rgba(201,168,76,0.04)',
    maxWidth: '340px',
  },
  insightText: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
    lineHeight: '1.7',
    fontStyle: 'italic',
  },
  factBox: {
    border: '0.5px solid rgba(255,255,255,0.1)',
    borderRadius: '14px',
    padding: '16px 20px',
    background: 'rgba(255,255,255,0.03)',
    maxWidth: '340px',
  },
  factLabel: {
    fontSize: '12px',
    color: 'rgba(201,168,76,0.8)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  factText: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
    lineHeight: '1.6',
  },
  loadingDots: {
    display: 'flex',
    gap: '8px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: 'var(--gold)',
    opacity: 0.6,
    display: 'inline-block',
  },
  errorText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '15px',
    textAlign: 'center',
    maxWidth: '300px',
    lineHeight: '1.6',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 0 20px',
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '15px',
    padding: '8px 0',
    cursor: 'pointer',
    minHeight: '44px',
  },
  logo: {
    fontSize: '14px',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  destHeader: {
    marginBottom: '16px',
  },
  destName: {
    fontSize: '28px',
    fontWeight: '500',
    color: '#fff',
    marginBottom: '4px',
    lineHeight: '1.2',
  },
  destLocation: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '10px',
  },
  profileTag: {
    display: 'inline-block',
    fontSize: '12px',
    color: 'var(--gold)',
    border: '0.5px solid rgba(201,168,76,0.4)',
    borderRadius: '20px',
    padding: '5px 12px',
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: '1.6',
    marginBottom: '24px',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '32px',
  },
  card: {
    border: '0.5px solid',
    borderRadius: '14px',
    background: '#1a1a35',
    overflow: 'hidden',
    transition: 'border-color 0.3s ease',
  },
  cardHeader: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    padding: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    gap: '12px',
    minHeight: '44px',
  },
  cardLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '6px',
    flex: 1,
    minWidth: 0,
  },
  activityName: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#fff',
    textAlign: 'left',
    lineHeight: '1.3',
  },
  typeBadge: {
    fontSize: '12px',
    borderRadius: '20px',
    padding: '4px 10px',
    textTransform: 'capitalize',
  },
  chevron: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.5)',
    flexShrink: 0,
  },
  activityCardRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  heartBtn: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
  cardBody: {
    padding: '0 16px 16px',
    borderTop: '0.5px solid rgba(255,255,255,0.06)',
    paddingTop: '14px',
  },
  description: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.75)',
    lineHeight: '1.7',
    marginBottom: '14px',
  },
  tipBox: {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
    padding: '12px 14px',
  },
  tipLabel: {
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '6px',
  },
  tipText: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.7)',
    lineHeight: '1.6',
  },
  backToResults: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    padding: '4px 0',
    cursor: 'pointer',
    minHeight: '44px',
  },
  paginationWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '24px',
  },
  continueBtn: {
    flex: 1,
    background: 'transparent',
    border: '1px solid var(--gold)',
    borderRadius: '24px',
    color: 'var(--gold)',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    minHeight: '44px',
    letterSpacing: '0.02em',
  },
  dotsRow: {
    display: 'flex',
    gap: '6px',
  },
  dot2: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    display: 'inline-block',
    transition: 'background 0.3s ease',
  },
  profileTagWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '10px',
  },
  activityProfileBanner: {
    border: '0.5px solid rgba(72,180,160,0.25)',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '16px',
    background: 'rgba(72,180,160,0.04)',
  },
  activityProfileName: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#48b4a0',
    marginBottom: '6px',
  },
  activityProfileDesc: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: '1.7',
  },
};
export default Activities;